import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { SparklesIcon, PencilSquareIcon, PlusIcon } from '@heroicons/react/24/outline';
import { baseAPI } from '../../config';
import { TASK_TYPE_LABELS } from '@claimup/shared/constants/assignments';

// Tarjeta (solo admin) con los prompts de IA específicos de esta company. Es
// solo lectura: cada prompt enlaza a la pantalla "AI Agent" para editarlo allí.
const CompanyPromptsCard = ({ userData }) => {
  const navigate = useNavigate();
  const [prompts, setPrompts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    if (!userData?._id) {
      setIsLoading(false);
      return;
    }
    const token = localStorage.getItem('token');
    axios
      .get(`${baseAPI}/ai-system-prompts?client_id=${userData._id}&active=true`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      })
      .then(res => setPrompts(res.data || []))
      .catch(() => setHasError(true))
      .finally(() => setIsLoading(false));
  }, [userData?._id]);

  const goToEdit = promptId => navigate(`/ai-agent?tab=prompts&promptId=${promptId}`);
  const goToCreate = () => navigate(`/ai-agent?tab=prompts&newCompanyId=${userData._id}`);

  return (
    <div className='bg-white rounded-lg border border-slate-200 overflow-hidden mt-6'>
      <div className='px-6 py-4 border-b border-slate-200 bg-slate-50 flex items-center justify-between'>
        <div className='flex items-center'>
          <SparklesIcon className='h-5 w-5 text-blue-600 mr-2' />
          <h3 className='text-lg font-medium text-slate-900'>Prompts de IA de esta empresa</h3>
        </div>
        <button
          type='button'
          onClick={goToCreate}
          className='inline-flex items-center text-sm font-medium text-blue-600 hover:text-blue-800'
        >
          <PlusIcon className='h-4 w-4 mr-1' />
          Crear prompt
        </button>
      </div>

      <div className='p-6'>
        {isLoading ? (
          <div className='flex justify-center py-4'>
            <div className='animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500'></div>
          </div>
        ) : hasError ? (
          <p className='text-sm text-red-600'>No se pudieron cargar los prompts. Inténtalo de nuevo.</p>
        ) : prompts.length === 0 ? (
          <p className='text-sm text-slate-400'>
            Esta empresa no tiene prompts propios. Usa los genéricos; crea uno para
            personalizar la IA solo para ella.
          </p>
        ) : (
          <div className='space-y-2'>
            {prompts.map(p => (
              <div
                key={p._id}
                className='flex items-center justify-between px-4 py-2 border border-slate-200 rounded-lg bg-white'
              >
                <div className='min-w-0'>
                  <p className='text-sm font-medium text-slate-800'>
                    {TASK_TYPE_LABELS[p.task_type] || p.task_type}
                    <span className='ml-2 text-xs text-slate-400'>v{p.version}</span>
                  </p>
                  <p className='text-xs text-slate-500 truncate'>
                    {p.description || `${p.ai_provider} / ${p.model_id}`}
                  </p>
                </div>
                <button
                  type='button'
                  onClick={() => goToEdit(p._id)}
                  className='inline-flex items-center text-sm font-medium text-blue-600 hover:text-blue-800 shrink-0 ml-3'
                >
                  <PencilSquareIcon className='h-4 w-4 mr-1' />
                  Editar
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default CompanyPromptsCard;
